<script setup lang="ts">
// The map's shaping tools, as one pane of the right-hand tool rail: the
// zones (drawn and imported) and the Buffers / temporary zones. The rail's
// other panes keep what the operator
// reads the map BY — the layer-visibility rows and the finder — and every tool
// that writes, shapes or ships records lives here.
//
// The state the tools write stays in the view, because the view owns the map:
// the draw composables are bound to the map's own events, the zone form's draft
// is what the map's overlay draws, and a buffer is resolved from the ledger's
// shared spec. This component therefore holds the markup and the form's own
// fields (the zone form's name and comment by v-model, the buffer's distance,
// unit and surface as local state) and reports every action upward through its
// emits.
//
// The `.control-pane-*` class names are the pane's own, kept verbatim from the
// markup these tools have moved through, so the rows read as the layer rows do.
import { computed, ref, watch } from "vue";

import { useStoreMapReview } from "@/store/map-review";
import type {
	MapReviewBufferCombine,
	MapReviewLengthUnit,
	MapReviewTable,
	MapReviewTempZone,
} from "@/type/api";

import { MAP_REVIEW_TABLE_LABELS, MAP_REVIEW_TABLES } from "./columns";

interface Props {
	/** The zone draw is armed on the map. */
	zoneArmed: boolean;
	/** A ring has been drawn and is waiting to be named. */
	zoneHasDraft: boolean;
	/** The zone the form is correcting, "" while it is making a new one. */
	zoneCorrectingName: string;
	/** Whether the form is complete enough to write: a name, and a ring unless
	 * it is a correction (whose geometry the server re-states when omitted). */
	zoneSaveable: boolean;
	zoneSaving: boolean;
	zoneImporting: boolean;
	/** The district's live units, as the zone list endpoint names them: the chips
	 * can only name a zone that is on the map. */
	zoneOptions: { id: number; name: string }[];
	bufferBusy: boolean;
	tempZoneSaving: boolean;
}

interface Emits {
	"toggle-zone-draw": [];
	"clear-zone-filter": [];
	"toggle-zone": [id: number];
	"zone-import-file": [file: File];
	"save-zone": [];
	"discard-zone": [];
	/** Materialise a buffer from the kept rows of the stated surface. */
	buffer: [
		request: {
			distance: number;
			unit: MapReviewLengthUnit;
			source: MapReviewTable;
		},
	];
	"save-buffer-zone": [zone: MapReviewTempZone];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const store = useStoreMapReview();

// The spec is the one source of truth for the tools, so the pane reads what
// is in force rather than being told: the zones it would narrow to and the
// buffers it would list.
const zoneSelection = computed<number[]>(() => store.spec.zones ?? []);
const tempZones = computed<MapReviewTempZone[]>(
	() => store.spec.temp_zones ?? [],
);

/** How the inside buffers compose, as the spec states it (union when absent). */
const bufferCombine = computed<MapReviewBufferCombine>(
	() => store.spec.buffer_combine ?? "union",
);

const BUFFER_COMBINES: { value: MapReviewBufferCombine; label: string }[] = [
	{ value: "union", label: "Union" },
	{ value: "intersection", label: "Intersection" },
];

function onBufferCombineChange(event: Event): void {
	const value = (event.target as HTMLSelectElement).value;
	if (value === "union" || value === "intersection") {
		store.setBufferCombine(value);
	}
}

/** Whether the zone form is open: a drawn ring or a correction is in progress. */
const zoneFormOpen = computed(
	() => props.zoneHasDraft || props.zoneCorrectingName !== "",
);

/** The draw button's label: the tool's own state, and what its next click will
 * do — a correction keeps the unit's own shape until a ring replaces it. */
const zoneDrawLabel = computed(() => {
	if (props.zoneArmed) return "Cancel";
	if (props.zoneHasDraft) return "Redraw";
	return props.zoneCorrectingName !== "" ? "Redraw ring" : "Draw zone";
});

function zoneSelected(id: number): boolean {
	return zoneSelection.value.includes(id);
}

// The zone form's two fields: the view owns them (a correction opens the form
// on the unit's own name and comment), so they travel by v-model.
const zoneName = defineModel<string>("zoneName", { required: true });
const zoneComment = defineModel<string>("zoneComment", { required: true });

/**
 * The import's file picker: a native file input cannot be styled into the tool
 * row, so the button opens it. One file at a time — the endpoint takes one. The
 * chosen file is handed straight up, and the input is cleared as soon as it is,
 * so choosing the same file twice still fires `change`.
 */
const zoneFileInput = ref<HTMLInputElement | null>(null);

function openZoneImport(): void {
	zoneFileInput.value?.click();
}

function onZoneFileChosen(event: Event): void {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	input.value = "";
	if (!file) return;
	emit("zone-import-file", file);
}

/** The surfaces a buffer may be built from: the tab list less Zones. A zone has
 * no geometry of its own to buffer, so it is the one surface the server refuses
 * as a source; every other ledger carries a geometry. */
const SPATIAL_TABLES: MapReviewTable[] = MAP_REVIEW_TABLES.filter(
	(table) => table !== "zone",
);

/** The distances a buffer may be stated in, in the operator's words. The token
 * is the wire's vocabulary — the server parses the same four — so the label
 * changes and the value never does. */
const BUFFER_UNITS: { value: MapReviewLengthUnit; label: string }[] = [
	{ value: "m", label: "Metres" },
	{ value: "km", label: "Kilometres" },
	{ value: "ft", label: "Feet" },
	{ value: "mi", label: "Miles" },
];

// The Buffer form: how far, in what unit, from which surface. The surface
// starts on the tab the operator is reading — a buffer is built from the ledger
// in front of them — and follows the tab only until they choose another, after
// which the tab no longer moves what they picked.
const bufferDistance = ref<number | null>(null);
const bufferUnit = ref<MapReviewLengthUnit>("km");
const bufferSource = ref<MapReviewTable>(
	SPATIAL_TABLES.includes(store.activeTable) ? store.activeTable : "feature",
);
const bufferSourceChosen = ref(false);

watch(
	() => store.activeTable,
	(table) => {
		if (!bufferSourceChosen.value && SPATIAL_TABLES.includes(table)) {
			bufferSource.value = table;
		}
	},
);

/** The stated distance as the server wants it, or null while the form states
 * none. Zero, negatives and non-numbers are the server's own refusals, so the
 * form must not send them; the button reads this too, which is what keeps it
 * inert until a distance is stated. */
const bufferDistanceValue = computed<number | null>(() => {
	const stated = bufferDistance.value;
	return typeof stated === "number" && Number.isFinite(stated) && stated > 0
		? stated
		: null;
});

function materializeBuffer(): void {
	const distance = bufferDistanceValue.value;
	if (props.bufferBusy || distance === null) return;
	emit("buffer", {
		distance,
		unit: bufferUnit.value,
		source: bufferSource.value,
	});
}
</script>

<template>
	<div class="control-pane">
		<!-- The zone filter, built as one row: a label and its actions, then
		     the units themselves. Selecting a zone writes the shared spec, so it
		     narrows the grid, the counts and the map together and selecting
		     several narrows to their union. The chips come from the zone list
		     endpoint, and the map's polygon layer is the `zone` tile map, so the
		     filter can only name a unit that is on the map. -->
		<div class="control-pane-tool">
			<span class="control-pane-tool-label">
				Zones{{ zoneSelection.length > 0 ? ` (${zoneSelection.length})` : "" }}
			</span>
			<button
				type="button"
				class="control-pane-btn"
				:class="{ active: zoneArmed || zoneFormOpen }"
				aria-label="Draw zone"
				title="Draw a ring, then name it to create a zone"
				@click="emit('toggle-zone-draw')"
			>
				{{ zoneDrawLabel }}
			</button>
			<button
				type="button"
				class="control-pane-btn"
				:disabled="zoneImporting"
				aria-label="Import zones"
				title="Import zones from a KML, KMZ or zipped Shapefile"
				@click="openZoneImport"
			>
				{{ zoneImporting ? "Importing…" : "Import" }}
			</button>
			<button
				v-if="zoneSelection.length > 0"
				type="button"
				class="control-pane-btn"
				aria-label="Clear zone filter"
				title="Clear zone filter"
				@click="emit('clear-zone-filter')"
			>
				Clear
			</button>
			<input
				ref="zoneFileInput"
				type="file"
				class="d-none"
				accept=".kml,.kmz,.zip"
				@change="onZoneFileChosen"
			/>
		</div>
		<div v-if="zoneOptions.length > 0" class="control-pane-zone-list">
			<button
				v-for="zone in zoneOptions"
				:key="zone.id"
				type="button"
				class="control-pane-btn"
				:class="{ active: zoneSelected(zone.id) }"
				:aria-pressed="zoneSelected(zone.id)"
				:title="`Show only records inside ${zone.name}`"
				@click="emit('toggle-zone', zone.id)"
			>
				{{ zone.name }}
			</button>
		</div>
		<!-- The drawn ring, named and commented before it becomes a zone: a
		     unit is made once and correctly, and corrected by this same form,
		     so nothing is written until the district says what the unit is. A
		     drawn ring stays on the map while the form is open; a correction
		     that only renames sends no geometry, and the server keeps the
		     unit's own shape. -->
		<form
			v-if="zoneFormOpen"
			class="control-pane-zone-form"
			@submit.prevent="emit('save-zone')"
		>
			<div v-if="zoneCorrectingName" class="control-pane-zone-mode">
				Correcting "{{ zoneCorrectingName }}"
			</div>
			<input
				v-model="zoneName"
				class="control-pane-zone-input"
				type="text"
				placeholder="Zone name"
				aria-label="Zone name"
			/>
			<input
				v-model="zoneComment"
				class="control-pane-zone-input"
				type="text"
				placeholder="Comment (optional)"
				aria-label="Zone comment"
			/>
			<button
				type="submit"
				class="control-pane-btn"
				:disabled="zoneSaving || zoneName.trim() === '' || !zoneSaveable"
			>
				{{
					zoneSaving
						? "Saving…"
						: zoneCorrectingName
							? "Save version"
							: "Save zone"
				}}
			</button>
			<button
				type="button"
				class="control-pane-btn"
				@click="emit('discard-zone')"
			>
				Discard
			</button>
		</form>

		<!-- The Buffer tool, beside the Zones tool and built the same
		     way: the label names the action, the row states the whole of it —
		     how far, in what unit, from which surface — and the list below
		     holds what it materialised. A buffer is made from the ledger the
		     operator is reading, so the source starts on the active tab and
		     follows it until they choose another. Nothing here computes
		     geometry or narrows a layer: the server resolves the kept rows,
		     the composition and the ground distance, and the spec carries the
		     result. -->
		<div class="control-pane-tool control-pane-tool--buffer">
			<span class="control-pane-tool-label">
				Buffer{{ tempZones.length > 0 ? ` (${tempZones.length})` : "" }}
			</span>
			<input
				v-model.number="bufferDistance"
				class="control-pane-buffer-distance"
				type="number"
				min="0"
				step="any"
				placeholder="Distance"
				aria-label="Buffer distance"
				title="How far to buffer the kept rows"
			/>
			<select
				v-model="bufferUnit"
				class="control-pane-buffer-select"
				aria-label="Buffer distance unit"
				title="The unit the distance is stated in: metres, kilometres, feet or miles"
			>
				<option
					v-for="unit in BUFFER_UNITS"
					:key="unit.value"
					:value="unit.value"
				>
					{{ unit.label }}
				</option>
			</select>
			<select
				v-model="bufferSource"
				class="control-pane-buffer-select control-pane-buffer-source"
				aria-label="Buffer source surface"
				title="The surface whose kept rows are buffered"
				@change="bufferSourceChosen = true"
			>
				<option v-for="table in SPATIAL_TABLES" :key="table" :value="table">
					{{ MAP_REVIEW_TABLE_LABELS[table] }}
				</option>
			</select>
			<select
				class="control-pane-buffer-select"
				:value="bufferCombine"
				:disabled="tempZones.length < 2"
				aria-label="Buffer combination"
				title="How several inside buffers compose: union keeps rows in any, intersection keeps only rows in every one. Standing zones always compose by union."
				@change="onBufferCombineChange"
			>
				<option
					v-for="combine in BUFFER_COMBINES"
					:key="combine.value"
					:value="combine.value"
				>
					{{ combine.label }}
				</option>
			</select>
			<button
				type="button"
				class="control-pane-btn"
				:disabled="bufferBusy || bufferDistanceValue === null"
				aria-label="Materialise buffer"
				title="Materialise a temporary zone from the kept rows"
				@click="materializeBuffer"
			>
				{{ bufferBusy ? "Buffering…" : "Buffer" }}
			</button>
		</div>
		<!-- What the Buffer tool has made. Each is named by the server's own
		     label for what was buffered — the distance and the subject — and
		     carries the two things an operator does with one: save it as a
		     standing zone, which is the doc's bridge between the throwaway
		     unit and the kept one, or clear it. Clearing is the whole of the
		     temporary zones' life, so the Clear here drops every one of them
		     rather than the single row it sits beside. -->
		<div v-if="tempZones.length > 0" class="control-pane-temp-zone-list">
			<div
				v-for="(zone, index) in tempZones"
				:key="index"
				class="control-pane-temp-zone"
			>
				<span class="control-pane-temp-zone-name" :title="zone.name">
					{{ zone.name }}
				</span>
				<button
					type="button"
					class="control-pane-btn"
					:class="{ active: zone.inside !== false }"
					:title="
						zone.inside === false
							? 'This buffer EXCLUDES the rows it contains; click to keep them instead'
							: 'This buffer KEEPS the rows it contains; click to exclude them instead'
					"
					@click="store.setTempZoneInside(index, zone.inside === false)"
				>
					{{ zone.inside === false ? "Outside" : "Inside" }}
				</button>
				<button
					type="button"
					class="control-pane-btn"
					:disabled="tempZoneSaving"
					:title="`Save this buffer as a standing zone: ${zone.name}`"
					@click="emit('save-buffer-zone', zone)"
				>
					Save as zone
				</button>
				<button
					type="button"
					class="control-pane-btn"
					title="Clear every temporary zone"
					@click="store.clearTempZones()"
				>
					Clear
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* The pane's own base: the rail body owns the box — its width, background,
   padding and scroll — so the pane is only its content and its type scale. The
   `.control-pane-*` names are the rail panes' own, re-declared here because a
   scoped rule never reaches into another component's markup. */
.control-pane {
	font-size: 0.8125rem;
}

/* The tool rows: a label and its controls in one row, separated from the row
   above by a rule. The first row opens the pane, with the tab's title in the
   rail's own header rather than in the pane, so it has nothing above it to be
   separated from. */
.control-pane-tool {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 6px;
	padding-top: 6px;
	border-top: 1px solid #f1f3f5;
}

.control-pane-tool:first-child {
	margin-top: 0;
	padding-top: 0;
	border-top: 0;
}

.control-pane-tool-label {
	font-weight: 600;
	color: #495057;
	flex-shrink: 0;
}

.control-pane-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.control-pane-btn:hover {
	background: #f1f3f5;
}

.control-pane-btn.active {
	background: #e7f5ff;
	border-color: #74c0fc;
	color: #1864ab;
}

.control-pane-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

/* The zone filter's chips: one per unit the district drew or imported, wrapping
   because a district names more units than one row holds. The chip is the
   zone's name, which is how the district knows it; the id it toggles is what
   the spec carries. */
.control-pane-zone-list {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 4px;
}

/* The zone draw's name and comment, under the tool row that started it: the
   drawn ring is committed here and the unit is created only once it is named. */
.control-pane-zone-form {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 4px;
}

.control-pane-zone-input {
	flex: 1 1 100%;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 3px 8px;
	font-size: 0.8125rem;
}

/* The form's mode line. A correction writes a new version of a unit that
   already exists, which the operator must be able to tell from a form that
   would make a new one, so the form says which unit it is about to supersede. */
.control-pane-zone-mode {
	flex: 1 1 100%;
	color: #495057;
	font-size: 0.75rem;
	font-weight: 600;
}

/* The Buffer tool's own controls. The pane is 320px, so the whole statement —
   how far, in what unit, from which surface — cannot always fit one line: the
   row wraps, and the fields keep their own widths while wrapping rather than
   squeezing a unit or a surface name down to nothing. The action lands last,
   under the fields it applies to, as the zone form's Save does. */
.control-pane-tool--buffer {
	flex-wrap: wrap;
}

.control-pane-buffer-distance {
	flex: 0 0 4.25rem;
	min-width: 0;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 3px 6px;
	font-size: 0.75rem;
}

.control-pane-buffer-select {
	flex: 0 0 auto;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 2px 4px;
	font-size: 0.75rem;
}

/* The surface is the one field whose name is long and whose list is longest, so
   it takes the line's spare width and is the first to wrap onto the next. */
.control-pane-buffer-source {
	flex: 1 1 7rem;
	min-width: 7rem;
}

/* Each materialised buffer, under the tool that made it: the server's label for
   what was buffered — the distance and the subject, in the district's own
   terms — on its own line, with the two things an operator does with one, save
   it as a standing zone or clear it, beneath. The label is the whole statement,
   so it is allowed the full width and as many lines as it needs. */
.control-pane-temp-zone-list {
	margin-top: 4px;
}

.control-pane-temp-zone {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	margin-top: 4px;
}

.control-pane-temp-zone-name {
	flex: 1 1 100%;
	color: #495057;
	font-size: 0.75rem;
	font-weight: 600;
}
</style>
